import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

class StudioCrossSelling extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.quantityControls = new WeakSet();
    if (this.closest('.boost-sd__product-item')) {
      // Keep app card navigation from swallowing the independent card controls.
      for (const type of ['click', 'change', 'submit']) this.addEventListener(type, event => event.stopPropagation(), { signal });
    }
    this.addEventListener('change', event => {
      const select = event.target.closest('select[name="id"]');
      if (select) this.updateVariant(select);
    }, { signal });
    this.addEventListener('submit', event => {
      if (!event.target.matches('.cs-crosssell-form')) return;
      event.preventDefault();
      this.addToCart(event.target);
    }, { signal });
    this.addEventListener('click', event => {
      const tab = event.target.closest('[data-crosssell-tab]');
      if (tab) { this.activateTab(tab.dataset.crosssellTab); return; }
      const button = event.target.closest('[data-scroll]');
      if (!button) return;
      const track = button.closest('.cs-crosssell-stream').querySelector('.cs-crosssell-track');
      track.scrollBy({ left: Number(button.dataset.scroll) * track.clientWidth * .9, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }, { signal });
    this.addEventListener('keydown', event => {
      const tab = event.target.closest('[data-crosssell-tab]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
      const tabs = [...this.querySelectorAll('[data-crosssell-tab]')];
      const index = tabs.indexOf(tab);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + tabs.length) % tabs.length;
      event.preventDefault();
      this.activateTab(tabs[next].dataset.crosssellTab);
      tabs[next].focus();
    }, { signal });
    this.mobileStreams = matchMedia('(max-width:749px)');
    this.mobileStreams.addEventListener('change', () => { this.syncStreamLayout(); this.updateArrows(); }, { signal });
    this.setupTabs();
    this.setupQuantities();
    this.setupTracks();
    if ((this.dataset.recommendationUrl && !this.dataset.loaded) || this.querySelector('[data-collection-stream]:not([data-loaded])')) {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.observer.disconnect();
          this.loadStreams();
        }
      }, { rootMargin: '500px' });
      this.observer.observe(this);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
  }

  setupTabs() {
    const list = this.querySelector('.cs-crosssell-tabs');
    if (!list) return;
    const panels = [...this.querySelectorAll('[data-crosssell-panel]')];
    const available = panels.filter(panel => panel.querySelector('.cs-crosssell-card'));
    const selected = available.some(panel => panel.id === this.activePanel) ? this.activePanel : available[0]?.id;
    const focusedPanel = list.contains(document.activeElement) ? document.activeElement.dataset.crosssellTab : null;
    const buttons = available.map(panel => {
      const button = document.createElement('button');
      button.type = 'button'; button.id = panel.id + '-tab';
      button.dataset.crosssellTab = panel.id;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      const label = document.createElement('span');
      label.textContent = panel.querySelector('h3').textContent;
      const count = document.createElement('span');
      count.className = 'cs-crosssell-tab-count';
      count.setAttribute('aria-hidden', 'true');
      count.textContent = panel.querySelectorAll('.cs-crosssell-card').length;
      button.append(label, count);
      return button;
    });
    list.replaceChildren(...buttons);
    list.hidden = !available.length;
    this.classList.toggle('cs-cross-selling--tabbed', available.length > 0);
    for (const panel of panels) {
      panel.hidden = panel.id !== selected;
      if (available.includes(panel)) {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', panel.id + '-tab');
      }
    }
    if (selected) this.activateTab(selected);
    if (focusedPanel) buttons.find(button => button.dataset.crosssellTab === focusedPanel)?.focus();
  }

  activateTab(panelId) {
    this.activePanel = panelId;
    for (const button of this.querySelectorAll('[data-crosssell-tab]')) {
      const active = button.dataset.crosssellTab === panelId;
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    }
    this.syncStreamLayout();
    this.updateArrows();
  }

  syncStreamLayout() {
    const list = this.querySelector('.cs-crosssell-tabs');
    if (!list) return;
    const mobile = this.mobileStreams?.matches ?? matchMedia('(max-width:749px)').matches;
    const panels = [...this.querySelectorAll('[data-crosssell-panel]')];
    list.hidden = mobile || !panels.some(panel => panel.querySelector('.cs-crosssell-card'));
    for (const panel of panels) {
      const populated = Boolean(panel.querySelector('.cs-crosssell-card'));
      panel.hidden = !populated || (!mobile && panel.id !== this.activePanel);
      if (mobile || !populated) {
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
      } else {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', panel.id + '-tab');
      }
    }
  }

  setupTracks() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    for (const track of this.querySelectorAll('.cs-crosssell-track')) {
      this.resizeObserver.observe(track);
      if (!track.dataset.bound) {
        track.dataset.bound = 'true';
        track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal: this.controller.signal });
      }
    }
    this.updateArrows();
  }

  updateArrows() {
    this.querySelector('.cs-crosssell-tabs')?.setAttribute('aria-orientation', 'horizontal');
    for (const stream of this.querySelectorAll('.cs-crosssell-stream')) {
      const track = stream.querySelector('.cs-crosssell-track');
      const arrows = stream.querySelector('.cs-crosssell-arrows');
      arrows.hidden = track.scrollWidth <= track.clientWidth + 2;
      stream.querySelector('[data-scroll="-1"]').disabled = track.scrollLeft < 2;
      stream.querySelector('[data-scroll="1"]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    }
  }


  async loadStreams() {
    const jobs = [...this.querySelectorAll('[data-collection-stream]:not([data-loaded])')]
      .map(panel => this.loadCollectionStream(panel));
    if (this.dataset.recommendationUrl && !this.dataset.loaded) jobs.push(this.loadRecommendations());
    await Promise.allSettled(jobs);
    if (this.controller.signal.aborted) return;
    this.setupTabs();
    this.setupQuantities();
    this.setupTracks();
    this.hidden = !this.querySelector('.cs-crosssell-card');
  }

  async loadCollectionStream(panel) {
    try {
      const url = new URL(panel.dataset.collectionUrl, window.location.origin);
      url.searchParams.set('sort_by', 'best-selling');
      url.searchParams.set('section_id', 'studio-crosssell-collection');
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Collection unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('.cs-crosssell-stream');
      if (incoming) {
        const cards = [...incoming.querySelectorAll('.cs-crosssell-card')];
        let count = 0;
        for (const card of cards) {
          if (card.dataset.productId === this.dataset.currentProductId || count >= 8) card.remove();
          else count++;
        }
        if (count) {
          // Scope fetched control IDs to this panel, including labels and ARIA references.
          const ids = new Map();
          for (const element of incoming.querySelectorAll('[id]')) {
            const oldId = element.id;
            element.id = panel.id + '-' + oldId;
            ids.set(oldId, element.id);
          }
          for (const element of [incoming, ...incoming.querySelectorAll('[for], [aria-labelledby], [aria-controls], [aria-describedby]')]) {
            for (const attribute of ['for', 'aria-labelledby', 'aria-controls', 'aria-describedby']) {
              if (!element.hasAttribute(attribute)) continue;
              element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map(id => ids.get(id) || id).join(' '));
            }
          }
          panel.replaceChildren(incoming);
        }
      }
      panel.dataset.loaded = 'true';
    } catch (error) {
      if (error.name !== 'AbortError') panel.dataset.collectionUnavailable = 'true';
    }
  }

  async loadRecommendations() {
    try {
      const response = await fetch(this.dataset.recommendationUrl, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = html.querySelector('[data-frequent-stream]');
      const target = this.querySelector('[data-frequent-stream]');
      if (incoming && target) target.replaceChildren(...incoming.childNodes);
      this.dataset.loaded = 'true';
      this.setupTabs();
      this.setupQuantities();
      this.setupTracks();

    } catch (error) {
      if (error.name !== 'AbortError') this.dataset.recommendationsUnavailable = 'true';
    }
  }

  updateVariant(select) {
    const option = select.selectedOptions[0];
    if (!option) return;
    const card = select.closest('.cs-crosssell-card');
    card.querySelector('[data-card-price]').textContent = option.dataset.price;
    const comparePrice = option.dataset.compare || '';
    card.querySelector('[data-card-compare]').textContent = comparePrice;
    card.querySelector('[data-card-compare-wrap]').hidden = !comparePrice;
    card.querySelector('.cs-crosssell-pricing').dataset.discounted = String(Boolean(comparePrice));
    card.querySelector('[data-card-unit]').textContent = option.dataset.unit;
    const quantity = card.querySelector('[name="quantity"]');
    quantity.min = option.dataset.min;
    quantity.step = option.dataset.step;
    quantity.max = option.dataset.max;
    if (!quantity.checkValidity()) quantity.value = option.dataset.min;
    this.syncQuantity(quantity);
    const purchase = card.querySelector('[data-listing-price-limit]');
    if (purchase) {
      const detailsOnly = Number(option.dataset.priceAmount) >= Number(purchase.dataset.listingPriceLimit);
      purchase.toggleAttribute('data-details-only', detailsOnly);
      purchase.querySelector('button[type="submit"]').disabled = detailsOnly || option.dataset.available !== 'true';
    }
    if (this.classList.contains('studio-listing-card')) {
      for (const link of card.querySelectorAll('.cs-crosssell-title, .cs-crosssell-image, .cs-crosssell-rating, .cs-card-details, .studio-listing-mobile-link')) {
        const url = new URL(link.href); url.searchParams.set('variant', option.value); link.href = url.href;
      }
    }
    const image = card.querySelector('.cs-crosssell-img');
    if (image && option.dataset.image) { image.src = option.dataset.image; image.removeAttribute('srcset'); }
    card.querySelector('.cs-crosssell-status').textContent = '';
  }


  setupQuantities() {
    for (const input of this.querySelectorAll('.cs-crosssell-quantity input')) {
      const root = input.parentElement;
      if (!root.querySelector('select')) {
        const select = document.createElement('select');
        select.className = 'cs-crosssell-quantity-select';
        select.setAttribute('aria-label', input.getAttribute('aria-label') || input.labels[0]?.textContent || 'Quantity');
        root.append(select);
        root.classList.add('is-enhanced');
      }
      const select = root.querySelector('select');
      if (!this.quantityControls.has(select)) {
        this.quantityControls.add(select);
        select.addEventListener('change', () => {
          if (select.value === 'custom') {
            root.classList.add('is-custom');
            input.focus(); input.select();
          } else {
            root.classList.remove('is-custom');
            input.value = select.value;
          }
        }, { signal: this.controller.signal });
        input.addEventListener('blur', () => this.syncQuantity(input), { signal: this.controller.signal });
      }
      this.syncQuantity(input);
    }
  }

  syncQuantity(input) {
    const select = input.parentElement.querySelector('select');
    if (!select) return;
    const min = Math.max(1, Number(input.min) || 1), step = Number(input.step) || 1;
    const max = input.max ? Number(input.max) : Infinity;
    const key = [min, step, max].join(':');
    if (select.dataset.constraints !== key) {
      const options = [];
      for (let value = min; value < 50 && value <= max; value += step) options.push(new Option(String(value), String(value)));
      if (max >= 50) options.push(new Option('50+', 'custom'));
      select.replaceChildren(...options);
      select.dataset.constraints = key;
    }
    const custom = ![...select.options].some(option => option.value === input.value);
    input.parentElement.classList.toggle('is-custom', custom);
    select.value = custom ? 'custom' : input.value;
  }

  async addToCart(form) {
    const button = form.querySelector('button[type="submit"]');
    if (!button || button.disabled || form.querySelector('[data-listing-price-limit][data-details-only]') || !form.reportValidity()) return;
    const labelElement = button.querySelector('.cs-card-cart-label') || button.querySelector('.add-to-cart-text');
    const label = labelElement.textContent;
    const status = form.querySelector('.cs-crosssell-status');
    status.textContent = ''; status.removeAttribute('data-error');
    const body = new FormData(form);
    const quantity = Number(body.get('quantity'));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map(el => el.dataset.sectionId).filter(Boolean);
    body.set('sections', [...new Set(sectionIds)].slice(0,5).join(','));
    body.set('sections_url', window.location.pathname);
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    labelElement.textContent = this.dataset.loadingLabel;
    const pending = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(body.get('id')), quantity }], promise: pending.promise }));
    let added = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, { method: 'POST', headers: { Accept: 'application/json' }, body });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorLabel);
      added = true;
      button.removeAttribute('aria-busy');
      labelElement.textContent = label;
      button.dataset.added = 'true';
      const resetFeedback = async () => {
        await yieldToMainThread();
        await onAnimationEnd(button);
        setTimeout(() => button.removeAttribute('data-added'), 800);
      };
      void resetFeedback();
      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error(this.dataset.errorLabel);
      const cart = await cartResponse.json();
      pending.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, itemCount: quantity, source: 'studio-cross-selling', sourceId: form.id, productId: form.closest('[data-product-id]').dataset.productId, sections: result.sections, didError: false } });
      status.textContent = this.dataset.addedLabel;
    } catch (error) {
      pending.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'CART_UPDATE_ERROR' }));
      status.textContent = added ? this.dataset.addedLabel : error.message || this.dataset.errorLabel;
      if (!added) status.setAttribute('data-error', '');
    } finally {
      button.disabled = false; button.removeAttribute('aria-busy'); labelElement.textContent = label;
    }
  }
}

if (!customElements.get('studio-cross-selling')) customElements.define('studio-cross-selling', StudioCrossSelling);
